import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { getStudentResultDetail } from '../../services/labResults.api';

const DEFAULT_MAX = 100;

function clampPct(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function sliceFromBreakdown(breakdown, key) {
  if (!breakdown || typeof breakdown !== 'object') return { score: null, maxScore: null };
  const s = breakdown[key];
  if (!s || typeof s !== 'object') return { score: null, maxScore: null };
  return {
    score: s.score != null && Number.isFinite(Number(s.score)) ? Number(s.score) : null,
    maxScore: s.maxScore != null && Number.isFinite(Number(s.maxScore)) ? Number(s.maxScore) : null
  };
}

function componentMax(detail, key, fallback = DEFAULT_MAX) {
  const fromBd = sliceFromBreakdown(detail?.breakdown, key).maxScore;
  if (fromBd != null && fromBd > 0) return fromBd;
  return fallback;
}

function componentPct(score, max) {
  if (max == null || max <= 0 || score == null || !Number.isFinite(score)) return 0;
  return clampPct((score / max) * 100);
}

function componentTone(pct) {
  if (pct >= 70) return 'strong';
  if (pct < 40) return 'weak';
  return 'mid';
}

function isPassingVerdict(verdict) {
  return typeof verdict === 'string' && verdict.trim().toUpperCase().startsWith('PASS');
}

function passFailChip(detail) {
  const st = String(detail?.status || '').toUpperCase();
  if (st === 'FAILED') return { label: 'FAILED', color: 'error' };
  if (st !== 'COMPLETED') return { label: '—', color: 'default' };
  if (isPassingVerdict(detail?.verdict)) return { label: 'PASSED', color: 'success' };
  return { label: 'FAILED', color: 'error' };
}

function normalizeFlagList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((x) => {
        if (typeof x === 'string') return x.trim();
        if (x && typeof x === 'object' && typeof x.message === 'string') return x.message.trim();
        try {
          return JSON.stringify(x);
        } catch {
          return String(x);
        }
      })
      .filter(Boolean);
  }
  return [];
}

function collectActionableIssues(detail) {
  const lines = [];
  const push = (prefix, items) => {
    for (const t of items) {
      if (t) lines.push(`${prefix}${t}`);
    }
  };

  const top = detail?.flags && typeof detail.flags === 'object' ? detail.flags : {};
  push('Critical: ', normalizeFlagList(top.critical));
  push('Warning: ', normalizeFlagList(top.warning));

  const ed = detail?.evaluationDetails && typeof detail.evaluationDetails === 'object' ? detail.evaluationDetails : {};
  for (const comp of ['git', 'api', 'ui']) {
    const block = ed[comp];
    if (!block || typeof block !== 'object') continue;
    const label = comp.charAt(0).toUpperCase() + comp.slice(1);
    const flags = normalizeFlagList(block.flags);
    push(`${label} / `, flags);
    const remarks = normalizeFlagList(block.remarks);
    for (const r of remarks) {
      lines.push(`${label}: ${r}`);
    }
  }

  const merged = Array.isArray(detail?.remarks) ? detail.remarks.filter((x) => typeof x === 'string' && x.trim()) : [];
  for (const r of merged) {
    if (!lines.includes(r)) lines.push(r);
  }

  if (detail?.error && typeof detail.error === 'string' && detail.error.trim()) {
    lines.push(`Submission error: ${detail.error.trim()}`);
  }

  return lines;
}

function JsonBlock({ value, maxHeight = 240 }) {
  const text =
    value === undefined || value === null
      ? '—'
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight,
        overflow: 'auto'
      }}
    >
      {text}
    </Box>
  );
}

function ApiResultsTable({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No test case rows returned.
      </Typography>
    );
  }
  const keys = [...new Set(rows.flatMap((r) => (r && typeof r === 'object' ? Object.keys(r) : [])))].slice(0, 12);
  if (keys.length === 0) {
    return <JsonBlock value={rows} maxHeight={280} />;
  }
  return (
    <TableContainer sx={{ borderRadius: 1, border: 1, borderColor: 'divider' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {keys.map((k) => (
              <TableCell key={k} sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                {k}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx} hover>
              {keys.map((k) => (
                <TableCell key={k}>
                  {row && typeof row === 'object' && row[k] != null && typeof row[k] === 'object'
                    ? JSON.stringify(row[k])
                    : row && row[k] != null
                      ? String(row[k])
                      : '—'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function StudentSubmissionResultPage() {
  const { submissionId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!submissionId) {
        setLoading(false);
        setDetail(null);
        setError('');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const d = await getStudentResultDetail(submissionId);
        if (!cancelled) {
          setDetail(d && typeof d === 'object' ? d : null);
          setError('');
        }
      } catch (e) {
        if (!cancelled) {
          setDetail(null);
          const msg = e?.response?.data?.message || e?.message || 'Failed to load';
          const code = e?.response?.status;
          setError(code === 404 ? 'No submission found' : msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  const statusUpper = String(detail?.status || '').toUpperCase();
  const inProgress = statusUpper === 'PENDING' || statusUpper === 'PROCESSING';

  const totalMax = useMemo(() => {
    const m = detail?.breakdown?.maxTotal;
    if (m != null && Number.isFinite(Number(m)) && Number(m) > 0) return Number(m);
    return DEFAULT_MAX;
  }, [detail?.breakdown]);

  const totalScore = detail?.totalScore != null && Number.isFinite(Number(detail.totalScore)) ? Number(detail.totalScore) : null;
  const totalPct = totalScore != null ? clampPct((totalScore / totalMax) * 100) : 0;

  const gitMax = useMemo(() => componentMax(detail, 'git', 20), [detail]);
  const apiMax = useMemo(() => componentMax(detail, 'api', 60), [detail]);
  const uiMax = useMemo(() => componentMax(detail, 'ui', 20), [detail]);

  const gitPct = componentPct(detail?.gitScore != null ? Number(detail.gitScore) : null, gitMax);
  const apiPct = componentPct(detail?.apiScore != null ? Number(detail.apiScore) : null, apiMax);
  const uiPct = componentPct(detail?.uiScore != null ? Number(detail.uiScore) : null, uiMax);

  const issues = useMemo(() => collectActionableIssues(detail || {}), [detail]);

  const ed = detail?.evaluationDetails && typeof detail.evaluationDetails === 'object' ? detail.evaluationDetails : {};
  const gitEd = ed.git && typeof ed.git === 'object' ? ed.git : {};
  const apiEd = ed.api && typeof ed.api === 'object' ? ed.api : {};
  const uiEd = ed.ui && typeof ed.ui === 'object' ? ed.ui : {};

  const outcome = detail ? passFailChip(detail) : { label: '—', color: 'default' };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
        <Alert severity={error === 'No submission found' ? 'warning' : 'error'} sx={{ mb: 2 }}>
          {error || 'No submission found'}
        </Alert>
        <Button component={RouterLink} to="/student/lab-results" variant="outlined" size="small">
          Back to my results
        </Button>
      </Box>
    );
  }

  if (inProgress) {
    return (
      <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
        <Button component={RouterLink} to="/student/lab-results" size="small" sx={{ mb: 2 }}>
          ← My results
        </Button>
        <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={2} alignItems="flex-start">
              <Chip label={statusUpper} color="info" variant="outlined" />
              <Typography variant="h6" fontWeight={700}>
                Evaluation in progress…
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your submission is queued or being evaluated. Refresh this page in a few minutes to see scores and
                feedback.
              </Typography>
              <LinearProgress sx={{ width: '100%', borderRadius: 1 }} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 960, mx: 'auto', pb: 6 }}>
      <Button component={RouterLink} to="/student/lab-results" size="small" sx={{ mb: 2 }}>
        ← My results
      </Button>

      <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
        Submission result
      </Typography>

      {statusUpper === 'FAILED' && detail.error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {detail.error}
        </Alert>
      ) : null}

      {/* Summary */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 2px 12px rgba(15,23,42,0.06)'
        }}
      >
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Total score
              </Typography>
              <Typography variant="h3" fontWeight={800} color="primary.main" sx={{ lineHeight: 1.1 }}>
                {totalScore != null ? totalScore : '—'}
                <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 0.5, fontWeight: 600 }}>
                  / {totalMax}
                </Typography>
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Chip label={`Grade: ${detail.grade ?? '—'}`} variant="outlined" />
              <Chip label={outcome.label} color={outcome.color} />
              <Chip label={statusUpper} size="small" variant="outlined" />
            </Stack>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Overall progress
              </Typography>
              <Typography variant="caption" fontWeight={700}>
                {totalScore != null ? `${Math.round(totalPct)}%` : '—'}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={totalPct}
              sx={{
                height: 10,
                borderRadius: 99,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 99,
                  bgcolor: outcome.label === 'PASSED' ? 'success.main' : 'error.main'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Component breakdown */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Component breakdown
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {[
          { key: 'Git', score: detail.gitScore, max: gitMax, pct: gitPct },
          { key: 'API', score: detail.apiScore, max: apiMax, pct: apiPct },
          { key: 'UI', score: detail.uiScore, max: uiMax, pct: uiPct }
        ].map(({ key, score, max, pct }) => {
          const tone = componentTone(pct);
          const border =
            tone === 'weak' ? 'error.light' : tone === 'strong' ? 'success.light' : 'divider';
          return (
            <Card
              key={key}
              elevation={0}
              sx={{
                flex: 1,
                borderRadius: 2,
                border: 2,
                borderColor: border,
                bgcolor:
                  tone === 'weak'
                    ? (t) => (t.palette.mode === 'dark' ? 'rgba(211,47,47,0.12)' : 'rgba(211,47,47,0.06)')
                    : tone === 'strong'
                      ? (t) => (t.palette.mode === 'dark' ? 'rgba(46,125,50,0.14)' : 'rgba(46,125,50,0.07)')
                      : 'background.paper'
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography fontWeight={700}>{key}</Typography>
                  {tone === 'weak' ? (
                    <Chip size="small" label="Needs work" color="error" variant="outlined" />
                  ) : tone === 'strong' ? (
                    <Chip size="small" label="Strong" color="success" variant="outlined" />
                  ) : (
                    <Chip size="small" label="OK" variant="outlined" />
                  )}
                </Stack>
                <Typography variant="h6" fontWeight={700}>
                  {score != null && Number.isFinite(Number(score)) ? Number(score) : '—'}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    / {max}
                  </Typography>
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    mt: 1.5,
                    height: 8,
                    borderRadius: 99,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 99,
                      bgcolor: tone === 'weak' ? 'error.main' : tone === 'strong' ? 'success.main' : 'primary.main'
                    }
                  }}
                />
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Issues */}
      {issues.length > 0 ? (
        <Card elevation={0} sx={{ mb: 3, borderRadius: 2, border: 1, borderColor: 'warning.light' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Issues & feedback
            </Typography>
            <List dense disablePadding>
              {issues.map((line, i) => (
                <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                    <FlagOutlinedIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={line} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      ) : null}

      {detail.override ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          This result includes a faculty override. Automated scores are preserved for reference.
        </Alert>
      ) : null}

      {/* Accordions */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Detailed evaluation
      </Typography>

      <Accordion defaultExpanded disableGutters sx={{ borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>Git</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Breakdown
              </Typography>
              <JsonBlock value={detail.gitBreakdown ?? gitEd.breakdown ?? gitEd.details ?? '—'} maxHeight={220} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Flags
              </Typography>
              <JsonBlock value={gitEd.flags ?? []} maxHeight={120} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Remarks
              </Typography>
              <JsonBlock value={gitEd.remarks ?? []} maxHeight={160} />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters sx={{ borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>API</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Test case results
              </Typography>
              <ApiResultsTable rows={detail.apiResults || apiEd.results} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Breakdown
              </Typography>
              <JsonBlock value={detail.apiBreakdown ?? apiEd.breakdown ?? '—'} maxHeight={220} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Flags
              </Typography>
              <JsonBlock value={apiEd.flags ?? []} maxHeight={120} />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters sx={{ borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>UI</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Breakdown
              </Typography>
              <JsonBlock value={detail.uiBreakdown ?? uiEd.breakdown ?? '—'} maxHeight={220} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Flags
              </Typography>
              <JsonBlock value={uiEd.flags ?? []} maxHeight={120} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Remarks
              </Typography>
              <JsonBlock value={uiEd.remarks ?? []} maxHeight={160} />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters sx={{ borderRadius: '8px !important', '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>Rollup & metadata</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Verdict
              </Typography>
              <Typography variant="body2">{detail.verdict ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Evaluated at
              </Typography>
              <Typography variant="body2">
                {detail.evaluatedAt ? new Date(detail.evaluatedAt).toLocaleString() : '—'}
              </Typography>
            </Box>
            {detail.automatedEvaluation ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Automated evaluation (before override)
                </Typography>
                <JsonBlock value={detail.automatedEvaluation} maxHeight={120} />
              </Box>
            ) : null}
            {detail.override ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Override
                </Typography>
                <JsonBlock value={detail.override} maxHeight={160} />
              </Box>
            ) : null}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Full breakdown object
              </Typography>
              <JsonBlock value={detail.breakdown} maxHeight={200} />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
